/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Multi-Speaker Dialogue Script Editor Component (Task 10.5.5)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  Plus,
  Trash2,
  Play,
  Pause,
  ArrowUp,
  ArrowDown,
  Sparkles,
  Sliders,
  Clock,
  FileText,
  UserCheck,
  CheckCircle2,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import {
  useAudioMixerStore,
  DialogueBlock,
} from '../../stores/useAudioMixerStore';
import { useVoiceCatalogStore } from '../../stores/useVoiceCatalogStore';

export const MultiSpeakerDialogEditor: React.FC = () => {
  const { t } = useTranslation();
  const {
    speakers,
    dialogueBlocks,
    addSpeaker,
    deleteSpeaker,
    addDialogueBlock,
    updateDialogueBlock,
    deleteDialogueBlock,
    moveDialogueBlock,
    loadScriptTemplate,
    synthesizeDialogueBlock,
    synthesizeAllBlocks,
    getTotalWordCount,
    getEstimatedCredits,
    syncScriptToTimeline,
    setActiveStudioTab,
  } = useAudioMixerStore();

  const { voices } = useVoiceCatalogStore();

  const [activePreviewBlockId, setActivePreviewBlockId] = useState<string | null>(null);
  const [isSynthesizingAll, setIsSynthesizingAll] = useState(false);
  const [showAddSpeakerModal, setShowAddSpeakerModal] = useState(false);

  // New speaker draft state
  const [newSpeakerName, setNewSpeakerName] = useState('');
  const [newSpeakerVoiceId, setNewSpeakerVoiceId] = useState('openai-alloy');
  const [newSpeakerColor, setNewSpeakerColor] = useState('#6366F1');

  const totalWords = getTotalWordCount();
  const estimatedCredits = getEstimatedCredits();

  const handlePreviewToggle = (block: DialogueBlock) => {
    if (activePreviewBlockId === block.id) {
      setActivePreviewBlockId(null);
    } else {
      setActivePreviewBlockId(block.id);
      setTimeout(() => {
        setActivePreviewBlockId(null);
      }, (block.duration || 3) * 1000);
    }
  };

  const handleSynthesizeAll = async () => {
    setIsSynthesizingAll(true);
    await synthesizeAllBlocks();
    setIsSynthesizingAll(false);
  };

  const handleCreateSpeaker = () => {
    if (!newSpeakerName.trim()) return;
    const selectedVoice = voices.find((v) => v.id === newSpeakerVoiceId) || voices[0];
    addSpeaker({
      name: newSpeakerName.trim(),
      voiceId: selectedVoice?.id || 'openai-alloy',
      voiceName: selectedVoice?.name || 'Alloy (OpenAI)',
      provider: selectedVoice?.provider || 'openai',
      gender: selectedVoice?.gender || 'neutral',
      color: newSpeakerColor,
      defaultSpeed: 1.0,
      defaultPitch: 0,
    });
    setNewSpeakerName('');
    setShowAddSpeakerModal(false);
  };

  return (
    <div className="space-y-6">
      {/* Top Banner & Script Templates Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-2xl bg-surface border border-surface-border shadow-xs">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="size-8 rounded-xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center">
              <FileText className="size-4.5" />
            </div>
            <h2 className="text-base font-bold font-heading text-foreground">
              {t('mixer.scriptEditorTitle')}
            </h2>
          </div>
          <p className="text-xs text-foreground-muted">
            {t('mixer.scriptEditorSubtitle')}
          </p>
        </div>

        {/* Quick Script Preset Templates */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-semibold text-foreground-muted">
            {t('mixer.templatePresets')}:
          </span>
          <button
            type="button"
            onClick={() => loadScriptTemplate('podcast')}
            className="px-2.5 py-1 text-xs font-medium rounded-lg bg-surface-elevated text-foreground hover:bg-surface-border transition-colors"
          >
            {t('mixer.presetPodcast')}
          </button>
          <button
            type="button"
            onClick={() => loadScriptTemplate('news')}
            className="px-2.5 py-1 text-xs font-medium rounded-lg bg-surface-elevated text-foreground hover:bg-surface-border transition-colors"
          >
            {t('mixer.presetNews')}
          </button>
          <button
            type="button"
            onClick={() => loadScriptTemplate('drama')}
            className="px-2.5 py-1 text-xs font-medium rounded-lg bg-surface-elevated text-foreground hover:bg-surface-border transition-colors"
          >
            {t('mixer.presetDrama')}
          </button>
        </div>
      </div>

      {/* Speaker Roster Section */}
      <div className="p-4 rounded-2xl bg-surface border border-surface-border space-y-3 shadow-xs">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserCheck className="size-4 text-primary" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
              {t('mixer.speakerRosterTitle')} ({speakers.length})
            </h3>
          </div>
          <button
            type="button"
            onClick={() => setShowAddSpeakerModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 text-xs font-semibold transition-colors"
          >
            <Plus className="size-3.5" />
            <span>{t('mixer.addSpeakerBtn')}</span>
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {speakers.map((spk) => (
            <div
              key={spk.id}
              className="p-3 rounded-xl bg-surface-elevated border border-surface-border flex items-center justify-between gap-3 group"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <div
                  className="size-8 rounded-lg flex items-center justify-center text-xs font-bold text-white shrink-0 shadow-xs"
                  style={{ backgroundColor: spk.color }}
                >
                  {spk.name.slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-bold text-foreground truncate">
                    {spk.name}
                  </div>
                  <div className="text-[10px] text-foreground-muted truncate">
                    {spk.voiceName} &bull; {spk.gender}
                  </div>
                </div>
              </div>

              {speakers.length > 1 && (
                <button
                  type="button"
                  onClick={() => deleteSpeaker(spk.id)}
                  title={t('mixer.deleteSpeaker')}
                  className="p-1 rounded-md text-foreground-muted hover:text-red-500 hover:bg-surface transition-colors shrink-0 opacity-0 group-hover:opacity-100"
                >
                  <Trash2 className="size-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Dialogue Sequence List */}
      <div className="space-y-3">
        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-2 text-xs font-semibold text-foreground-muted">
            <span>{t('mixer.dialogueSequence')}</span>
            <span>&bull;</span>
            <span>{dialogueBlocks.length} {t('mixer.linesLabel')}</span>
            <span>&bull;</span>
            <span>{totalWords} {t('cat.wordsCountLabel')}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSynthesizeAll}
              disabled={isSynthesizingAll}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-elevated text-foreground hover:bg-surface-border text-xs font-semibold transition-colors disabled:opacity-50"
            >
              {isSynthesizingAll ? (
                <>
                  <div className="size-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <span>{t('mixer.synthesizingAll')}</span>
                </>
              ) : (
                <>
                  <Sparkles className="size-3.5 text-primary" />
                  <span>{t('mixer.synthesizeAllBtn')} (~{estimatedCredits} cr)</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                syncScriptToTimeline();
                setActiveStudioTab('timeline');
              }}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-primary text-primary-fg hover:bg-primary/90 text-xs font-semibold shadow-xs transition-colors"
            >
              <Sliders className="size-3.5" />
              <span>{t('mixer.openTimelineDaw')}</span>
            </button>
          </div>
        </div>

        {/* Dialogue Blocks List */}
        <div className="space-y-3">
          {dialogueBlocks.map((block, index) => {
            const currentSpeaker =
              speakers.find((s) => s.id === block.speakerId) || speakers[0];
            const isPlayingThis = activePreviewBlockId === block.id;

            return (
              <div
                key={block.id}
                className="p-4 rounded-xl bg-surface border border-surface-border hover:border-surface-border/80 transition-all space-y-3"
              >
                {/* Block Header: Speaker Selector, Reorder, Delete */}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  {/* Speaker Selector */}
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-mono text-foreground-muted font-bold">
                      #{index + 1}
                    </span>
                    <div
                      className="size-3 rounded-full shrink-0"
                      style={{ backgroundColor: currentSpeaker?.color || '#6366F1' }}
                    />
                    <select
                      value={block.speakerId}
                      onChange={(e) =>
                        updateDialogueBlock(block.id, { speakerId: e.target.value })
                      }
                      className="px-2.5 py-1 rounded-lg bg-surface-elevated border border-surface-border text-xs font-bold text-foreground focus:outline-none focus:border-primary cursor-pointer"
                    >
                      {speakers.map((spk) => (
                        <option key={spk.id} value={spk.id}>
                          {spk.name}
                        </option>
                      ))}
                    </select>

                    {/* Timing Badge */}
                    <span className="text-[10px] font-mono font-medium px-2 py-0.5 rounded-md bg-surface-elevated text-foreground-muted border border-surface-border">
                      {block.startTime}s &rarr; {block.endTime}s ({block.duration}s)
                    </span>
                  </div>

                  {/* Actions: Synthesize Line, Preview Audio, Reorder, Delete */}
                  <div className="flex items-center gap-1.5">
                    {/* Status Pill */}
                    {block.status === 'ready' && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                        <CheckCircle2 className="size-2.5" />
                        {t('mixer.statusReady')}
                      </span>
                    )}

                    {/* Preview Audio Line */}
                    <button
                      type="button"
                      onClick={() => handlePreviewToggle(block)}
                      className={`p-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${
                        isPlayingThis
                          ? 'bg-primary text-primary-fg'
                          : 'bg-surface-elevated text-foreground-muted hover:text-foreground hover:bg-surface-border'
                      }`}
                    >
                      {isPlayingThis ? (
                        <Pause className="size-3.5" />
                      ) : (
                        <Play className="size-3.5 fill-current" />
                      )}
                    </button>

                    {/* Synthesize Single Line */}
                    <button
                      type="button"
                      onClick={() => synthesizeDialogueBlock(block.id)}
                      disabled={block.status === 'synthesizing'}
                      title={t('mixer.synthesizeLine')}
                      className="p-1.5 rounded-lg text-foreground-muted hover:text-primary hover:bg-surface-elevated transition-colors"
                    >
                      {block.status === 'synthesizing' ? (
                        <div className="size-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Sparkles className="size-3.5" />
                      )}
                    </button>

                    <div className="h-4 w-px bg-surface-border mx-1" />

                    {/* Move Up */}
                    <button
                      type="button"
                      disabled={index === 0}
                      onClick={() => moveDialogueBlock(index, index - 1)}
                      className="p-1.5 rounded-lg text-foreground-muted hover:text-foreground hover:bg-surface-elevated disabled:opacity-30 transition-colors"
                    >
                      <ArrowUp className="size-3.5" />
                    </button>

                    {/* Move Down */}
                    <button
                      type="button"
                      disabled={index === dialogueBlocks.length - 1}
                      onClick={() => moveDialogueBlock(index, index + 1)}
                      className="p-1.5 rounded-lg text-foreground-muted hover:text-foreground hover:bg-surface-elevated disabled:opacity-30 transition-colors"
                    >
                      <ArrowDown className="size-3.5" />
                    </button>

                    {/* Delete Line */}
                    <button
                      type="button"
                      onClick={() => deleteDialogueBlock(block.id)}
                      className="p-1.5 rounded-lg text-foreground-muted hover:text-red-500 hover:bg-surface-elevated transition-colors"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                </div>

                {/* Text Content Textarea */}
                <textarea
                  rows={2}
                  value={block.text}
                  onChange={(e) =>
                    updateDialogueBlock(block.id, { text: e.target.value })
                  }
                  placeholder={t('mixer.enterDialoguePlaceholder')}
                  className="w-full px-3 py-2 rounded-xl bg-surface-elevated border border-surface-border text-xs text-foreground placeholder:text-foreground-muted focus:outline-none focus:border-primary transition-colors resize-none leading-relaxed"
                />

                {/* Voice Parameter Adjustments: Speed, Pitch, Pause After */}
                <div className="flex flex-wrap items-center gap-4 pt-1 text-[11px] text-foreground-muted border-t border-surface-border">
                  {/* Speed */}
                  <div className="flex items-center gap-1.5">
                    <span>{t('mixer.speedLabel')}:</span>
                    <input
                      type="range"
                      min="0.8"
                      max="1.5"
                      step="0.05"
                      value={block.speed}
                      onChange={(e) =>
                        updateDialogueBlock(block.id, {
                          speed: parseFloat(e.target.value),
                        })
                      }
                      className="w-16 accent-primary cursor-pointer"
                    />
                    <span className="font-mono text-foreground font-semibold">
                      {block.speed.toFixed(2)}x
                    </span>
                  </div>

                  {/* Pitch */}
                  <div className="flex items-center gap-1.5">
                    <span>{t('mixer.pitchLabel')}:</span>
                    <input
                      type="range"
                      min="-6"
                      max="6"
                      step="1"
                      value={block.pitch}
                      onChange={(e) =>
                        updateDialogueBlock(block.id, {
                          pitch: parseInt(e.target.value, 10),
                        })
                      }
                      className="w-16 accent-primary cursor-pointer"
                    />
                    <span className="font-mono text-foreground font-semibold">
                      {block.pitch > 0 ? `+${block.pitch}` : block.pitch}st
                    </span>
                  </div>

                  {/* Pause After */}
                  <div className="flex items-center gap-1.5">
                    <Clock className="size-3 text-foreground-muted" />
                    <span>{t('mixer.pauseAfterLabel')}:</span>
                    <select
                      value={block.pauseAfter}
                      onChange={(e) =>
                        updateDialogueBlock(block.id, {
                          pauseAfter: parseFloat(e.target.value),
                        })
                      }
                      className="px-2 py-0.5 rounded bg-surface-elevated border border-surface-border text-[11px] text-foreground font-mono focus:outline-none cursor-pointer"
                    >
                      <option value="0.2">0.2s</option>
                      <option value="0.4">0.4s</option>
                      <option value="0.6">0.6s</option>
                      <option value="0.8">0.8s</option>
                      <option value="1.0">1.0s</option>
                      <option value="1.5">1.5s</option>
                      <option value="2.0">2.0s</option>
                    </select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Add Dialogue Block Button */}
        <button
          type="button"
          onClick={() => addDialogueBlock()}
          className="w-full py-3 rounded-xl border border-dashed border-surface-border hover:border-primary text-xs font-semibold text-foreground-muted hover:text-primary transition-all flex items-center justify-center gap-2 bg-surface/50 hover:bg-surface"
        >
          <Plus className="size-4" />
          <span>{t('mixer.addNewLineBtn')}</span>
        </button>
      </div>

      {/* Add Speaker Modal */}
      {showAddSpeakerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-md bg-surface border border-surface-border rounded-2xl shadow-2xl p-5 space-y-4">
            <h3 className="text-sm font-bold text-foreground">
              {t('mixer.addSpeakerModalTitle')}
            </h3>

            <div className="space-y-3">
              <div>
                <label className="block text-[11px] font-semibold text-foreground-muted mb-1">
                  {t('mixer.speakerNameLabel')}
                </label>
                <input
                  type="text"
                  value={newSpeakerName}
                  onChange={(e) => setNewSpeakerName(e.target.value)}
                  placeholder={t('mixer.speakerNamePlaceholder')}
                  className="w-full px-3 py-2 rounded-xl bg-surface-elevated border border-surface-border text-xs text-foreground focus:outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-foreground-muted mb-1">
                  {t('mixer.assignVoiceLabel')}
                </label>
                <select
                  value={newSpeakerVoiceId}
                  onChange={(e) => setNewSpeakerVoiceId(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-surface-elevated border border-surface-border text-xs text-foreground focus:outline-none focus:border-primary cursor-pointer"
                >
                  {voices.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name} ({v.provider} &bull; {v.languageLabel})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-foreground-muted mb-1">
                  {t('mixer.trackColorLabel')}
                </label>
                <div className="flex items-center gap-2">
                  {['#06B6D4', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'].map(
                    (color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setNewSpeakerColor(color)}
                        className={`size-6 rounded-full border-2 transition-transform ${
                          newSpeakerColor === color ? 'border-foreground scale-110' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    )
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-surface-border">
              <button
                type="button"
                onClick={() => setShowAddSpeakerModal(false)}
                className="px-3 py-1.5 text-xs font-medium text-foreground-muted hover:text-foreground rounded-lg"
              >
                {t('cat.cancelBtn')}
              </button>
              <button
                type="button"
                onClick={handleCreateSpeaker}
                disabled={!newSpeakerName.trim()}
                className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-primary text-primary-fg hover:bg-primary/90 disabled:opacity-50 shadow-xs"
              >
                {t('mixer.saveSpeakerBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
